import CryptoJS from "crypto-js";
import pako from "pako";

const ENCRYPT_CONTENT_START = 0x01;

/**
 * Decode Logan encrypted log file (same logic as LoganProtocol.java)
 * @param {ArrayBuffer} buffer - raw encrypted file content
 * @param {string} aesKey - 16-char AES key
 * @param {string} aesIv - 16-char AES IV
 * @returns {Array<{c: string, f: string, l: string}>} parsed log items
 */
export function decodeLoganFile(buffer, aesKey = "0123456789012345", aesIv = "0123456789012345") {
  const data = new Uint8Array(buffer);
  const key = CryptoJS.enc.Utf8.parse(aesKey);
  const iv = CryptoJS.enc.Utf8.parse(aesIv);
  const logItems = [];
  let offset = 0;

  while (offset < data.length) {
    // Find ENCRYPT_CONTENT_START marker
    if (data[offset] !== ENCRYPT_CONTENT_START) {
      offset++;
      continue;
    }
    offset++;

    // Read 4-byte big-endian length
    if (offset + 4 > data.length) break;
    const len = (data[offset] << 24) | (data[offset + 1] << 16) | (data[offset + 2] << 8) | data[offset + 3];
    offset += 4;

    if (len <= 0 || offset + len > data.length) break;

    // Extract encrypted block
    const encrypted = data.slice(offset, offset + len);
    offset += len;

    try {
      const decrypted = aesDecrypt(encrypted, key, iv);
      if (!decrypted || decrypted.length === 0) continue;

      const decompressed = safeDecompress(decrypted);
      if (!decompressed) continue;

      // Parse JSON lines
      const text = new TextDecoder("utf-8").decode(decompressed);
      const lines = text.split("\n").filter(l => l.trim().length > 0);
      for (const line of lines) {
        try {
          const item = JSON.parse(line);
          logItems.push(item);
        } catch (e) {
          // skip malformed lines
        }
      }
    } catch (e) {
      // skip failed blocks
      console.warn("Failed to decrypt/decompress block", e);
    }
  }

  return logItems;
}

function aesDecrypt(encrypted, key, iv) {
  const wordArray = byteArrayToWordArray(encrypted);
  const cipherParams = CryptoJS.lib.CipherParams.create({ ciphertext: wordArray });

  // Try with padding first
  try {
    const decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    const result = wordArrayToByteArray(decrypted);
    if (result.length > 0) return result;
  } catch (e) {
    // Fall through to NoPadding
  }

  // Try without padding
  try {
    const decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.NoPadding,
    });
    return wordArrayToByteArray(decrypted);
  } catch (e) {
    return null;
  }
}

function safeDecompress(data) {
  try {
    return pako.ungzip(new Uint8Array(data));
  } catch (e) {
    // Partial decompression - try inflate
    try {
      return pako.inflate(new Uint8Array(data));
    } catch (e2) {
      return null;
    }
  }
}

function byteArrayToWordArray(bytes) {
  const words = [];
  for (let i = 0; i < bytes.length; i += 4) {
    words.push(
      ((bytes[i] || 0) << 24) |
      ((bytes[i + 1] || 0) << 16) |
      ((bytes[i + 2] || 0) << 8) |
      (bytes[i + 3] || 0)
    );
  }
  return CryptoJS.lib.WordArray.create(words, bytes.length);
}

function wordArrayToByteArray(wordArray) {
  const words = wordArray.words;
  const sigBytes = wordArray.sigBytes;
  const bytes = new Uint8Array(sigBytes);
  for (let i = 0; i < sigBytes; i++) {
    bytes[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
  }
  return bytes;
}
